import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Input'
import { GestorFeedbackEvidenceField } from '@/components/Gestores/GestorFeedbackEvidenceField'
import {
  GESTOR_FEEDBACK_AREA_OPTIONS,
  createEmptyFeedbackDocumentForm,
  type GestorFeedbackDocumentFormValues,
} from '@/components/Gestores/gestorFeedbackConstants'
import { normalizeGestorFeedbackEvidencePath } from '@/components/Gestores/gestorFeedbackEvidenceUtils'
import type { GestorEquipeFeedback, GestorFeedbackGestorOption } from '@/services/gestoresService'

type Props = {
  pjId: string
  profissionalNome: string
  gestores: GestorFeedbackGestorOption[]
  initial?: GestorEquipeFeedback | null
  saving?: boolean
  onSubmit: (values: GestorFeedbackDocumentFormValues) => Promise<void>
  onUploadEvidence: (file: File) => Promise<{
    evidenciaUrl: string
    evidenciaFileName: string
    evidenciaMimeType: string
  }>
  onCancel?: () => void
}

function mapFeedbackToForm(feedback: GestorEquipeFeedback): GestorFeedbackDocumentFormValues {
  return {
    authorId: feedback.author.id,
    dataFeedback: (feedback.dataFeedback ?? feedback.createdAt).slice(0, 10),
    areaAtuacao: feedback.areaAtuacao ?? '',
    situacao: feedback.situacao ?? feedback.conteudo,
    evidenciaUrl: feedback.evidenciaUrl
      ? normalizeGestorFeedbackEvidencePath(feedback.evidenciaUrl)
      : null,
    evidenciaFileName: feedback.evidenciaFileName,
    evidenciaMimeType: feedback.evidenciaMimeType,
    evidenciaLink: feedback.evidenciaLink ?? '',
    reportado: feedback.reportado,
    dataReport: feedback.dataReport?.slice(0, 10) ?? '',
    observacoesReporte: feedback.observacoesReporte ?? '',
  }
}

export function GestorFeedbackDocumentForm({
  pjId,
  profissionalNome,
  gestores,
  initial = null,
  saving = false,
  onSubmit,
  onUploadEvidence,
  onCancel,
}: Props) {
  const defaultAuthorId = gestores[0]?.id ?? ''
  const [values, setValues] = useState<GestorFeedbackDocumentFormValues>(
    initial ? mapFeedbackToForm(initial) : createEmptyFeedbackDocumentForm(defaultAuthorId),
  )

  useEffect(() => {
    setValues(
      initial ? mapFeedbackToForm(initial) : createEmptyFeedbackDocumentForm(defaultAuthorId),
    )
  }, [initial, defaultAuthorId])

  const patch = (patchValues: Partial<GestorFeedbackDocumentFormValues>) => {
    setValues((current) => ({ ...current, ...patchValues }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await onSubmit(values)
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className='space-y-4 rounded-xl border border-border bg-white/[0.02] p-4'>
      <div className='grid gap-4 md:grid-cols-2'>
        <label className='flex flex-col gap-1.5 text-sm'>
          <span className='font-medium text-text-muted'>Quem pontuou</span>
          <select
            value={values.authorId}
            onChange={(event) => patch({ authorId: event.target.value })}
            className='rounded-lg border border-border bg-bg-main px-3 py-2 text-sm text-text-primary'
            required
          >
            {gestores.map((gestor) => (
              <option key={gestor.id} value={gestor.id}>
                {gestor.name}
              </option>
            ))}
          </select>
        </label>

        <Input
          label='Data'
          type='date'
          value={values.dataFeedback}
          onChange={(event) => patch({ dataFeedback: event.target.value })}
          required
        />

        <Input label='Profissional' value={profissionalNome} readOnly disabled />

        <label className='flex flex-col gap-1.5 text-sm'>
          <span className='font-medium text-text-muted'>Área</span>
          <select
            value={values.areaAtuacao}
            onChange={(event) => patch({ areaAtuacao: event.target.value })}
            className='rounded-lg border border-border bg-bg-main px-3 py-2 text-sm text-text-primary'
            required
          >
            <option value=''>Selecione…</option>
            {GESTOR_FEEDBACK_AREA_OPTIONS.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Textarea
        label='Situação (descrição do que gerou o feedback)'
        value={values.situacao}
        onChange={(event) => patch({ situacao: event.target.value })}
        rows={4}
        required
      />

      <div>
        <p className='mb-2 text-xs font-medium uppercase tracking-wider text-text-muted'>Evidência (print ou link)</p>
        <GestorFeedbackEvidenceField
          pjId={pjId}
          evidenciaUrl={values.evidenciaUrl}
          evidenciaFileName={values.evidenciaFileName}
          evidenciaLink={values.evidenciaLink}
          onUpload={async (file) => {
            const uploaded = await onUploadEvidence(file)
            patch(uploaded)
          }}
          onLinkChange={(evidenciaLink) => patch({ evidenciaLink })}
          onClear={() =>
            patch({
              evidenciaUrl: null,
              evidenciaFileName: null,
              evidenciaMimeType: null,
              evidenciaLink: '',
            })
          }
        />
      </div>

      <div className='grid gap-4 md:grid-cols-2'>
        <label className='flex flex-col gap-1.5 text-sm'>
          <span className='font-medium text-text-muted'>Reportado</span>
          <select
            value={values.reportado ? 'sim' : 'nao'}
            onChange={(event) =>
              patch({
                reportado: event.target.value === 'sim',
                dataReport: event.target.value === 'sim' ? values.dataReport : '',
              })
            }
            className='rounded-lg border border-border bg-bg-main px-3 py-2 text-sm text-text-primary'
          >
            <option value='nao'>Não</option>
            <option value='sim'>Sim</option>
          </select>
        </label>

        <Input
          label='Data report'
          type='date'
          value={values.dataReport}
          onChange={(event) => patch({ dataReport: event.target.value })}
          disabled={!values.reportado}
        />
      </div>

      <Textarea
        label='Observações (onde foi reportado, registros necessários, etc.)'
        value={values.observacoesReporte}
        onChange={(event) => patch({ observacoesReporte: event.target.value })}
        rows={3}
      />

      <div className='flex flex-wrap gap-2'>
        <Button type='submit' size='sm' loading={saving}>
          {initial ? 'Salvar alterações' : 'Registrar feedback'}
        </Button>
        {onCancel && (
          <Button type='button' size='sm' variant='secondary' onClick={onCancel}>
            Cancelar
          </Button>
        )}
      </div>
    </form>
  )
}
